import { LIMITS, type DigitalDocument, type DigitalDocumentBlock } from '@worki/shared';

export type DigitalField = Extract<DigitalDocumentBlock, { type: 'field' }>;
export type Preset = 'name' | 'phone' | 'id' | 'companyId' | 'email' | 'address' | 'city' | 'date' | 'signature' | 'agreement' | 'checkbox' | 'yesNo' | 'custom';
export const FIELD_PRESETS: Preset[] = ['name', 'phone', 'email', 'city', 'id', 'companyId', 'address', 'date', 'signature', 'agreement', 'checkbox', 'yesNo', 'custom'];
export const FIELD_TYPES: DigitalField['fieldType'][] = ['text', 'textarea', 'phone', 'email', 'id', 'companyId', 'date', 'number', 'checkbox', 'select', 'signature'];

/** A field as one of the presets adds it, named `label` — the preset's name in the screen's language. */
export function newDigitalField(preset: Preset, label: string, yesNo: [string,string] = ['Yes','No']): DigitalField {
  const fieldType: DigitalField['fieldType'] = preset === 'name' || preset === 'address' || preset === 'city' || preset === 'custom' ? 'text' : preset === 'agreement' ? 'checkbox' : preset === 'yesNo' ? 'select' : preset;
  // A signature is asked for because it is wanted; the rest are optional unless said otherwise.
  return { id: crypto.randomUUID(), type: 'field', fieldType, label, required: preset === 'name' || preset === 'phone' || preset === 'id' || preset === 'signature' || preset === 'agreement', ...(preset === 'yesNo' ? {options:yesNo,purpose:'yesNo' as const} : {}), ...(preset === 'city' ? {purpose:'city' as const} : {}), ...(preset==='agreement'?{purpose:'agreement' as const}:{}) };
}

type ContentBlock=Exclude<DigitalDocumentBlock,{type:'field'}>;
export function formatDigitalBlock(block:ContentBlock,format:NonNullable<ContentBlock['format']>):ContentBlock {return {...block,format:{...block.format,...format}};}
export function quickDigitalFields(labels:Record<'name'|'phone'|'email'|'city'|'id',string>):DigitalField[] {
  return (['name','phone','email','city','id'] as const).map(preset=>newDigitalField(preset,labels[preset]));
}
export function changeDigitalFieldType(field:DigitalField,fieldType:DigitalField['fieldType'],defaultOptions:[string,string]):DigitalField {
  if(field.purpose==='agreement')return field;
  return {...field,fieldType,
    purpose:field.purpose==='city'&&fieldType==='text'?'city':field.purpose==='yesNo'&&fieldType==='select'?'yesNo':undefined,
    options:fieldType==='select'?field.options??defaultOptions:undefined,
  };
}
export function importTermsBlocks(text:string,heading:string,agreement:string):DigitalDocumentBlock[] {
  const normalized=text.replace(/\r\n?/g,'\n').trim();
  if(!normalized||normalized.length>49_000)throw new Error('Invalid terms text');
  return [{id:crypto.randomUUID(),type:'heading',text:heading},{id:crypto.randomUUID(),type:'paragraph',text:normalized},newDigitalField('agreement',agreement)];
}

/** How many more places this document may still ask to be signed in. */
export function signaturesLeft(fields: DigitalField[]): number {
  return LIMITS.maxDocumentSignatureFields - fields.filter(field => field.fieldType === 'signature').length;
}

export function duplicateDigitalBlock(block: DigitalDocumentBlock): DigitalDocumentBlock {
  return { ...block, id: crypto.randomUUID(), ...(block.type === 'field' && block.options ? { options: [...block.options] } : {}), ...(block.type!=='field'&&block.format?{format:{...block.format}}:{}) };
}

/** Duplicates carry structure, never another customer's submitted answers. */
export function digitalDocumentForCopy(structured: DigitalDocument | null | undefined, legacyBody: string): DigitalDocument {
  if (structured) {
    const ids = new Map(structured.blocks.map(block => [block.id, crypto.randomUUID()]));
    return { version: 1, blocks: structured.blocks.map(block => {
      const copied = { ...duplicateDigitalBlock(block), id: ids.get(block.id)! };
      if (copied.type !== 'field') for (const [oldId, newId] of ids) copied.text = copied.text.replaceAll(`{{${oldId}}}`, `{{${newId}}}`);
      return copied;
    }) };
  }
  return { version: 1, blocks: legacyBody.trim() ? [{ id: crypto.randomUUID(), type: 'paragraph', text: legacyBody }] : [] };
}

export function moveDigitalBlock(document: DigitalDocument, id: string, offset: -1 | 1): DigitalDocument {
  const index = document.blocks.findIndex((block) => block.id === id);
  const destination = index + offset;
  if (index < 0 || destination < 0 || destination >= document.blocks.length) return document;
  const blocks = [...document.blocks];
  const [block] = blocks.splice(index, 1);
  blocks.splice(destination, 0, block!);
  return { version: 1, blocks };
}
